<svelte:options
	customElement={{
		tag: "newsletter-form",
		shadow: "none",
	}}
/>

<script lang="ts">
	import { onMount } from "svelte";
	import ky from "ky";
	import { enforce, test } from "vest";
	import "vest/email";
	import { control, ValidatingForm } from "@wagich/form-validation";

	import { initFluentContext, Localized, localize } from "@/localization";
	import { FormState } from "@/utils";

	interface NewsletterSignupRequest {
		name: string;
		email: string;
		segment: string;
	}

	const initialFormData: NewsletterSignupRequest = {
		name: "",
		email: "",
		segment: "",
	};

	let formState: FormState = $state(FormState.Ready);
	let token: string = "";

	initFluentContext();

	const form = new ValidatingForm<NewsletterSignupRequest>(initialFormData, (data) => {
		test("name", localize("newsletterform-name.error-required"), () => {
			enforce(data.name).isNotBlank();
		});
		test("email", () => {
			enforce(data.email).message(localize("newsletterform-email.error-required")).isNotBlank().message(localize("newsletterform-email.error-format")).isEmail();
		});
		test("segment", localize("newsletterform-segment.error-required"), () => {
			enforce(data.segment).isNotBlank();
		});
	});

	onMount(() => {
		let tokenElement = document.getElementById("RequestVerificationToken");
		if (tokenElement == null || tokenElement.textContent == null) {
			throw new Error("Could not load XSRF token.");
		}
		token = JSON.parse(tokenElement.textContent);
	});

	async function submitAsync() {
		if (formState !== FormState.Ready) {
			return;
		}
		if (!form.validate()) {
			return;
		}

		try {
			formState = FormState.Loading;
			await ky.post(`/api/newsletter-signup`, {
				headers: { RequestVerificationToken: token },
				json: {
					...form.getData(),
					language: document.documentElement.lang,
                }
			});
			formState = FormState.Success;
			form.reset();
		} catch (e) {
			formState = FormState.Error;
		}
	}
</script>

<div class="newsletter-form">
	<div class="newsletter-form__header">
		<div class="title is-5 mb-1 has-text-white is-family-secondary">
			<Localized id="newsletterform-title" />
		</div>
		<p class="has-text-white">
			<Localized id="newsletterform-description" />
		</p>
	</div>

	<div class="field mb-0">
		<label for="newsletter-name" class="label is-small">
			<Localized id="newsletterform-name" />
		</label>
		<input id="newsletter-name" class="input" type="text" {@attach control(form.fields.name)} autocomplete="name" />
		{#each form.fields.name.errors as error}
			<p class="help is-danger">{error}</p>
		{/each}
	</div>
	<div class="field mb-0">
		<label for="newsletter-email" class="label is-small">
			<Localized id="newsletterform-email" />
		</label>
		<input id="newsletter-email" class="input" type="email" {@attach control(form.fields.email)} autocomplete="email" />
		{#each form.fields.email.errors as error}
			<p class="help is-danger">{error}</p>
		{/each}
	</div>
	<div class="field mb-0">
		<label class="label is-small">
			<Localized id="newsletterform-segment" />
		</label>
		<div class="control newsletter-form__segment-options">
            <div>
                <input type="radio" class="is-checkradio is-light" value="architect" id="newsletterSegmentArchitect" {@attach control(form.fields.segment)} />
                <label class="has-text-white" for="newsletterSegmentArchitect">
                    <Localized id="contactform-segment-architect" />
                </label>
            </div>
            <div>
                <input type="radio" class="is-checkradio is-light" value="consumer" id="newsletterSegmentConsumer" {@attach control(form.fields.segment)} />
                <label class="has-text-white" for="newsletterSegmentConsumer">
                    <Localized id="contactform-segment-consumer" />
                </label>
            </div>
		</div>
		{#each form.fields.segment.errors as error}
			<p class="help is-danger">{error}</p>
		{/each}
	</div>

	{#if formState === FormState.Ready || formState === FormState.Loading}
		<button type="button" class="button is-primary newsletter-form__submit" class:is-loading={formState === FormState.Loading} onclick={submitAsync}>
			<Localized id="newsletterform-submit" />
		</button>
	{:else if formState === FormState.Success}
		<div class="notification is-success mt-0">
			<Localized id="newsletterform-success" />
		</div>
	{:else if formState === FormState.Error}
		<div class="notification is-danger mt-0">
			<Localized id="newsletterform-error" />
		</div>
	{/if}
</div>
